import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import type { IManagedAccountSummary, IManagedAccountUserReference } from '../../../queries/ManagedAccount';
import {
	useManagedAccountAccessTokenMutation,
	useManagedAccountsQuery,
	useManagedAccountUsersQueries,
} from '../../../queries/ManagedAccount';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../components/DataGrid';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ChevronRight } from '../../components/svgs/icons/ChevronRight';
import { SearchIcon2 } from '../../components/svgs/icons/SearchIcon2';
import { Button } from '../../components-v2/Button';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../components-v2/Drawer/AppDrawer';
import { Input } from '../../components-v2/Input';
import { Page } from '../../components-v2/PageLayout';
import { DefaultUniversalAssistantChat } from '../../containers/templates/AssistantChat';
import {
	getUserReferenceDisplayName,
	isAccountAdminUser,
	switchToManagedAccountAccessSession,
} from '../../utils/managedAccountAccessSession';
import { styleSheet } from './styles';

interface IManagedAccountUsersState {
	isError: boolean;
	isLoading: boolean;
	refetch(): void;
	users: IManagedAccountUserReference[];
}

interface IPendingSignInTarget {
	accountId: string;
	userId: string;
}

function SignInIcon(props: React.SVGProps<SVGSVGElement>) {
	return (
		<svg
			aria-hidden='true'
			fill='none'
			height={16}
			stroke='currentColor'
			strokeLinecap='round'
			strokeLinejoin='round'
			strokeWidth={2}
			viewBox='0 0 24 24'
			width={16}
			{...props}
		>
			<path d='M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4' />
			<polyline points='10 17 15 12 10 7' />
			<line x1={15} x2={3} y1={12} y2={12} />
		</svg>
	);
}

function StateCard({ action, description, title }: { action?: React.ReactNode; description: string; title: string }) {
	return (
		<div className={css(styleSheet.stateCard)}>
			<div className={css(styleSheet.stateCardHeader)}>
				<h2 className={css(styleSheet.stateCardTitle)}>{title}</h2>
				<p className={css(styleSheet.description)}>{description}</p>
			</div>
			{action ? <div className={css(styleSheet.stateCardActions)}>{action}</div> : null}
		</div>
	);
}

function AssistantDrawer() {
	return (
		<AppDrawer>
			<StickyAppDrawerContent value='ai-assistant'>
				<DefaultUniversalAssistantChat />
			</StickyAppDrawerContent>
			<AppDrawerToolbar>
				<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
			</AppDrawerToolbar>
		</AppDrawer>
	);
}

function getAccountLabel(account?: IManagedAccountSummary | null) {
	return account?.companyName || account?.accountId || '';
}

function getManagedUserEmail(user?: IManagedAccountUserReference | null) {
	return user?.email || user?.primaryEmail?.value || '';
}

function getUserInitials(user: IManagedAccountUserReference) {
	const displayName = getUserReferenceDisplayName(user);
	const words = displayName.split(/\s+/).filter(Boolean);
	if (words.length === 0) {
		return getManagedUserEmail(user)[0]?.toUpperCase() || '?';
	}

	return words
		.slice(0, 2)
		.map(word => word[0])
		.join('')
		.toUpperCase();
}

function getUsersLabel({ isError, isLoading, users }: IManagedAccountUsersState) {
	if (isLoading) {
		return 'Loading users';
	}

	if (isError) {
		return 'Users unavailable';
	}

	return `${users.length} ${users.length === 1 ? 'user' : 'users'}`;
}

function accountMatchesSearch({
	account,
	searchQuery,
	users,
}: {
	account: IManagedAccountSummary;
	searchQuery: string;
	users: IManagedAccountUserReference[];
}) {
	const normalizedQuery = searchQuery.trim().toLowerCase();
	if (!normalizedQuery) {
		return true;
	}

	const accountSearchText = `${getAccountLabel(account)} ${account.accountId}`.toLowerCase();
	if (accountSearchText.includes(normalizedQuery)) {
		return true;
	}

	return users.some(user => {
		const userSearchText = `${getUserReferenceDisplayName(user)} ${getManagedUserEmail(user)}`.toLowerCase();
		return userSearchText.includes(normalizedQuery);
	});
}

function AccountRow({
	account,
	isExpanded,
	onSignIn,
	onToggle,
	pendingSignInTarget,
	usersState,
}: {
	account: IManagedAccountSummary;
	isExpanded: boolean;
	onSignIn(account: IManagedAccountSummary, user: IManagedAccountUserReference): void;
	onToggle(): void;
	pendingSignInTarget: IPendingSignInTarget | null;
	usersState: IManagedAccountUsersState;
}) {
	const accountLabel = getAccountLabel(account);
	const usersLabel = getUsersLabel(usersState);

	return (
		<>
			<DataGridRow>
				<DataGridRowItem styles={[styleSheet.accountCell]}>
					<Button
						aria-expanded={isExpanded}
						type='button'
						variant='ghost'
						styles={[styleSheet.accountToggle]}
						onClick={onToggle}
					>
						<ChevronRight className={css(styleSheet.chevron, isExpanded && styleSheet.chevronExpanded)} />
						<h3 className={css(styleSheet.accountName)}>{accountLabel}</h3>
					</Button>
				</DataGridRowItem>
				<DataGridRowItem styles={[styleSheet.usersCountCell]}>
					<span className={css(styleSheet.accountCount)}>{usersLabel}</span>
				</DataGridRowItem>
			</DataGridRow>
			{isExpanded ? (
				<DataGridRow>
					<DataGridRowItem fullColSpan noTopBorder styles={[styleSheet.expandedCell]}>
						<div className={css(styleSheet.usersPanel)}>
							{usersState.isLoading ? (
								<div className={css(styleSheet.panelState)}>
									<LoadingSpinner type='tiny' />
									Loading users...
								</div>
							) : usersState.isError ? (
								<div className={css(styleSheet.panelState)}>
									<span>We could not load users for this account.</span>
									<Button variant='secondary' size='sm' onClick={usersState.refetch}>
										Try again
									</Button>
								</div>
							) : usersState.users.length === 0 ? (
								<div className={css(styleSheet.panelState)}>No users are available for this managed account.</div>
							) : (
								<>
									{usersState.users.map(user => {
										const userName = getUserReferenceDisplayName(user) || user.id;
										const userEmail = getManagedUserEmail(user);
										const isSigningIn =
											pendingSignInTarget?.accountId === account.accountId && pendingSignInTarget.userId === user.id;
										return (
											<div className={css(styleSheet.userRow)} key={user.id}>
												<div className={css(styleSheet.userAvatar)}>{getUserInitials(user)}</div>
												<div className={css(styleSheet.userSummary)}>
													<div className={css(styleSheet.userName)}>{userName}</div>
													{userEmail ? <div className={css(styleSheet.userEmail)}>{userEmail}</div> : null}
												</div>
												<Button
													disabled={!!pendingSignInTarget}
													isLoading={isSigningIn}
													size='sm'
													type='button'
													styles={[styleSheet.signInButton]}
													onClick={() => onSignIn(account, user)}
												>
													<SignInIcon />
													Sign in
												</Button>
											</div>
										);
									})}
								</>
							)}
						</div>
					</DataGridRowItem>
				</DataGridRow>
			) : null}
		</>
	);
}

export function ManagedAccountsPage() {
	const userSession = useUserSession();
	const isAccountAdmin = isAccountAdminUser(userSession.user);
	const [expandedAccountId, setExpandedAccountId] = React.useState('');
	const [pendingSignInTarget, setPendingSignInTarget] = React.useState<IPendingSignInTarget | null>(null);
	const [searchQuery, setSearchQuery] = React.useState('');
	const managedAccountsQuery = useManagedAccountsQuery({
		enabled: isAccountAdmin,
		meta: {
			toastErrorMessage: 'Could not load managed accounts.',
		},
	});
	const managedAccounts = managedAccountsQuery.data ?? [];
	const managedAccountUsersQueries = useManagedAccountUsersQueries({
		enabled: isAccountAdmin && managedAccounts.length > 0,
		managedAccountIds: managedAccounts.map(account => account.accountId),
		meta: {
			toastErrorMessage: 'Could not load users for one or more managed accounts.',
		},
	});
	const accessTokenMutation = useManagedAccountAccessTokenMutation();

	const usersStateByAccountId = new Map<string, IManagedAccountUsersState>();
	managedAccounts.forEach((account, index) => {
		const usersQuery = managedAccountUsersQueries[index];
		usersStateByAccountId.set(account.accountId, {
			isError: usersQuery?.isError ?? false,
			isLoading: usersQuery?.isLoading ?? false,
			refetch: () => {
				usersQuery?.refetch();
			},
			users: usersQuery?.data ?? [],
		});
	});

	const sortedManagedAccounts = [...managedAccounts].sort((left, right) =>
		getAccountLabel(left).localeCompare(getAccountLabel(right))
	);
	const visibleManagedAccounts = sortedManagedAccounts.filter(account =>
		accountMatchesSearch({
			account,
			searchQuery,
			users: usersStateByAccountId.get(account.accountId)?.users ?? [],
		})
	);
	const isSearching = searchQuery.trim().length > 0;
	const isLoadingUserSearchData = managedAccountUsersQueries.some(query => query.isLoading);

	const onSignIn = async (managedAccount: IManagedAccountSummary, managedUser: IManagedAccountUserReference) => {
		if (!isAccountAdmin) {
			toast.error('Only account admins can sign in to managed accounts.');
			return;
		}

		setPendingSignInTarget({ accountId: managedAccount.accountId, userId: managedUser.id });
		try {
			const token = await accessTokenMutation.mutateAsync({
				managedAccountId: managedAccount.accountId,
				userId: managedUser.id,
			});
			await switchToManagedAccountAccessSession({
				managedAccount,
				managedUser,
				token,
				userSession,
			});
		} catch (error) {
			const apiError = Api.asApiError(error);
			toast.error(apiError.systemMessage || 'Unable to sign in to this managed account.');
		} finally {
			setPendingSignInTarget(null);
		}
	};

	if (!isAccountAdmin) {
		return (
			<Page styles={[styleSheet.page]}>
				<div className={css(styleSheet.container)}>
					<StateCard
						title='Managed accounts'
						description='Only account admins can sign in to managed customer accounts.'
					/>
				</div>
				<AssistantDrawer />
			</Page>
		);
	}

	if (managedAccountsQuery.isLoading) {
		return (
			<Page styles={[styleSheet.page]}>
				<div className={css(styleSheet.container, styleSheet.spinnerState)}>
					<LoadingSpinner />
				</div>
				<AssistantDrawer />
			</Page>
		);
	}

	if (managedAccountsQuery.isError) {
		return (
			<Page styles={[styleSheet.page]}>
				<div className={css(styleSheet.container)}>
					<StateCard
						title='Managed accounts'
						description='We could not load your managed accounts.'
						action={
							<Button variant='secondary' onClick={() => managedAccountsQuery.refetch()}>
								Try again
							</Button>
						}
					/>
				</div>
				<AssistantDrawer />
			</Page>
		);
	}

	if (managedAccounts.length === 0) {
		return (
			<Page styles={[styleSheet.page]}>
				<div className={css(styleSheet.container)}>
					<StateCard
						title='Managed accounts'
						description='No managed accounts are currently available for this account.'
					/>
				</div>
				<AssistantDrawer />
			</Page>
		);
	}

	return (
		<Page styles={[styleSheet.page]}>
			<div className={css(styleSheet.container)}>
				<div className={css(styleSheet.hero)}>
					<h1 className={css(styleSheet.title)}>Managed accounts</h1>
					<p className={css(styleSheet.subtitle)}>
						{managedAccounts.length} {managedAccounts.length === 1 ? 'client account' : 'client accounts'}
					</p>
				</div>

				<div className={css(styleSheet.searchShell)}>
					<SearchIcon2 className={css(styleSheet.searchIcon)} />
					<Input
						aria-label='Search managed accounts'
						placeholder='Search by account, user, or email'
						styles={[styleSheet.searchInput]}
						value={searchQuery}
						onChange={event => setSearchQuery(event.target.value)}
					/>
				</div>
				{isSearching ? (
					<div className={css(styleSheet.searchStatus)}>
						Showing {visibleManagedAccounts.length} of {managedAccounts.length} accounts
						{isLoadingUserSearchData ? ' while user data finishes loading' : ''}.
					</div>
				) : null}

				<section className={css(styleSheet.section)}>
					<div className={css(styleSheet.sectionHeader)}>
						<h2 className={css(styleSheet.sectionTitle)}>All clients</h2>
						{isSearching ? (
							<Button
								type='button'
								variant='link'
								styles={[styleSheet.clearSearchButton]}
								onClick={() => setSearchQuery('')}
							>
								Clear search
							</Button>
						) : null}
					</div>
					<div className={css(styleSheet.tableCard)}>
						<DataGrid>
							<DataGridColumns>
								<DataGridColumn>Account</DataGridColumn>
								<DataGridColumn styles={[styleSheet.usersColumn]}>Users</DataGridColumn>
							</DataGridColumns>
							<DataGridRows>
								{visibleManagedAccounts.length === 0 ? (
									<DataGridRow>
										<DataGridRowItem fullColSpan styles={[styleSheet.emptyRow]}>
											No accounts match your search.
										</DataGridRowItem>
									</DataGridRow>
								) : (
									visibleManagedAccounts.map(account => (
										<AccountRow
											account={account}
											isExpanded={expandedAccountId === account.accountId}
											key={account.accountId}
											pendingSignInTarget={pendingSignInTarget}
											usersState={
												usersStateByAccountId.get(account.accountId) ?? {
													isError: false,
													isLoading: true,
													refetch: () => {},
													users: [],
												}
											}
											onSignIn={onSignIn}
											onToggle={() =>
												setExpandedAccountId(previousAccountId =>
													previousAccountId === account.accountId ? '' : account.accountId
												)
											}
										/>
									))
								)}
							</DataGridRows>
						</DataGrid>
					</div>
				</section>
			</div>
			<AssistantDrawer />
		</Page>
	);
}
